import PrimaryButton from "@components/Buttons/PrimaryButton";
import { ScanToolSearchInfoContext } from "@components/Guards/ScanToolGuard";
import { ChevronUpIcon } from "@components/Icons";
import { Breadcrumb } from "@components/UI";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import useRequestDetail from "@hooks/useRequestDetail";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, formatFullDateTime, toTitleCase, formatVerificationType } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const TEST_ID = "request-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

const RequestDetail = () => {
  const { actionSeq, verificationTypeCd } = useParams();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch request details using the API
  const { requestData, isLoading, error } = useRequestDetail(actionSeq, verificationTypeCd);
  const { creatorList = [] } = useContext(ScanToolSearchInfoContext) ?? {};

  // State for accordion sections
  const [isRequestSummaryCollapsed, setIsRequestSummaryCollapsed] = useState(false);
  const [isRequestDetailsCollapsed, setIsRequestDetailsCollapsed] = useState(false);

  useEffect(() => {
    if (error && error.includes("404")) {
      navigate("/404", { state: { backTo: "/requests" } });
    }
  }, [error, navigate]);

  const findUserInfo = (userId) => {
    const userInfo = creatorList.find((user) => user.value === userId);
    return {
      name: userInfo?.label || userId || t("communication.unknown"),
      role: userInfo?.roleCdName || t("communication.unknown"),
    };
  };

  const buildConversation = (data) => {
    const details =
      Array.isArray(data?.binaryActionDetails) && data.binaryActionDetails.length > 0
        ? data.binaryActionDetails
        : Array.isArray(data?.sourceCodeActionDetails) && data.sourceCodeActionDetails.length > 0
          ? data.sourceCodeActionDetails
          : Array.isArray(data?.clarityAirActionDetails) && data.clarityAirActionDetails.length > 0
            ? data.clarityAirActionDetails
            : [];

    return details.map((item) => ({
      user: {
        name: findUserInfo(item.createrId).name,
        avatar: (findUserInfo(item.createrId).name || "-").charAt(0),
      },
      status: item.actionStatus || "-",
      timestamp: item.createdAt ? formatFullDateTime(item.createdAt) : "-",
      content: item.actionContent || "",
    }));
  };

  const getLatestDecision = (data) => {
    const details =
      Array.isArray(data?.binaryActionDetails) && data.binaryActionDetails.length > 0
        ? data.binaryActionDetails
        : Array.isArray(data?.sourceCodeActionDetails) && data.sourceCodeActionDetails.length > 0
          ? data.sourceCodeActionDetails
          : [];

    const decisions = details.filter((d) => d?.actionStatus === "Completed" || d?.actionStatus === "Rejected");
    if (decisions.length === 0) return null;

    decisions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const latest = decisions[0];
    return {
      status: latest.actionStatus,
      userId: latest.createrId,
      createdAt: latest.createdAt,
    };
  };

  const getLatestByStatus = (data, targetStatus) => {
    const details =
      Array.isArray(data?.binaryActionDetails) && data.binaryActionDetails.length > 0
        ? data.binaryActionDetails
        : Array.isArray(data?.sourceCodeActionDetails) && data.sourceCodeActionDetails.length > 0
          ? data.sourceCodeActionDetails
          : [];

    const items = details.filter((d) => d?.actionStatus === targetStatus);
    if (items.length === 0) return null;
    items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return items[0];
  };

  const renderVerificationStatusCell = (status, row = null) => {
    const statusStyles = {
      ON_ANALYSIS: colors.textStatusAnalysis,
      ANALYSIS_CHECK: colors.textStatusAnalysis,
      REQUEST: colors.textStatusRequest,
      ON_VERIFICATION: colors.textStatusVerification,
      ON_REVIEW: colors.textStatusReview,
      STANDBY_FOLLOWUP: colors.textStatusFollowup,
      FOLLOWUP_CHECK: colors.textStatusFollowup,
      DONE: colors.textStatusSuccess,
      REVERIFICATION: colors.textStatusReverification,
      ERROR: colors.textStatusAnalysis,
    };

    const statusColorDot = {
      ON_ANALYSIS: colors.bgStatusAnalysis,
      ANALYSIS_CHECK: colors.bgStatusAnalysis,
      REQUEST: colors.bgStatusRequest,
      ON_VERIFICATION: colors.bgStatusVerification,
      ON_REVIEW: colors.bgStatusReview,
      STANDBY_FOLLOWUP: colors.bgStatusFollowup,
      FOLLOWUP_CHECK: colors.bgStatusFollowup,
      DONE: colors.bgStatusSuccess,
      REVERIFICATION: colors.bgStatusReverification,
      ERROR: colors.bgStatusAnalysis,
    };

    const statusText = {
      ON_ANALYSIS: t("status.analyzing"),
      ANALYSIS_CHECK: t("status.analysisCheck"),
      REQUEST: t("status.request"),
      ON_VERIFICATION: t("status.onVerification"),
      ON_REVIEW: t("status.onReview"),
      STANDBY_FOLLOWUP: t("status.standbyFollowup"),
      FOLLOWUP_CHECK: t("status.followupCheck"),
      DONE: t("status.done"),
      REVERIFICATION: t("status.reverification"),
      ERROR: t("status.error"),
    };

    // For ON_VERIFICATION status, use the specific status name if available
    let displayText = statusText[status];
    if (status === "ON_VERIFICATION" && row) {
      const statusNameMap = {
        "Usage Type Review": t("status.usageTypeReview"),
        "Usage Type Check": t("status.usageTypeCheck"),
        "Usage Type Input": t("status.usageTypeInput"),
      };

      displayText = statusNameMap[row] || row;
    }

    return (
      <div data-testid={fieldValueTestId("verification-status")}>
        <span
          className={`inline-flex items-center py-0.5 text-sm text-[13px] leading-none font-semibold ${statusStyles[status] || ""}`}
        >
          {displayText ? (
            <>
              <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[status]}`}></div>
              {displayText}
            </>
          ) : (
            ""
          )}
        </span>
      </div>
    );
  };

  // Fallback data structure if API data is not available
  const getRequestData = () => {
    if (requestData) {
      return {
        requestId: requestData.actionId || actionSeq,
        projectId: requestData.projectId || "-",
        projectSeq: requestData.projectSeq || "-",
        projectName: requestData.projectName || "-",
        verificationFileName: requestData.realFileName || "-",
        verificationType: requestData.verificationType || verificationTypeCd,
        verificationStatusCdName: requestData.verificationStatusCdName || "-",
        verificationStatusCd: requestData.verificationStatusCd || "-",
        completedBy: (() => {
          const latest = getLatestByStatus(requestData, "Completed");
          return latest ? findUserInfo(latest.createrId).name : "-";
        })(),
        completedAt: (() => {
          const latest = getLatestByStatus(requestData, "Completed");
          return latest ? formatDateTime(latest.createdAt) : "-";
        })(),
        rejectedBy: (() => {
          const latest = getLatestByStatus(requestData, "Rejected");
          return latest ? findUserInfo(latest.createrId).name : "-";
        })(),
        rejectedAt: (() => {
          const latest = getLatestByStatus(requestData, "Rejected");
          return latest ? formatDateTime(latest.createdAt) : "-";
        })(),
        // New data for REQUEST DETAILS section
        createdOn: requestData.createdAt ? formatDateTime(requestData.createdAt) : "-",
        status: requestData.actionStatus || "-",
        latestDecision: getLatestDecision(requestData),
        requesterInfo: {
          name: findUserInfo(requestData.createrId).name || "-",
          role: findUserInfo(requestData.createrId).role || "-",
          avatar: findUserInfo(requestData.createrId).name.charAt(0) || "-",
        },
        assigneeInfo: {
          name: findUserInfo(requestData.assigneeId).name || "-",
          role: findUserInfo(requestData.assigneeId).role || "-",
          avatar: findUserInfo(requestData.assigneeId).name.charAt(0) || "-",
        },
        description: requestData.actionsContent || "",
        conversation: buildConversation(requestData),
        completionRequestDate: formatDate(requestData.completionRequestDt) || "-",
      };
    }
  };

  const isNotFound = Boolean(error && error.includes("404"));

  const currentRequestData = getRequestData();

  const handleGoToProject = () => {
    if (!requestData.projectSeq || !requestData.verificationSeq) {
      return;
    }
    const normalizedType =
    verificationTypeCd === "clarityair" || verificationTypeCd === "clarity_air" ? "clarity-air" : verificationTypeCd;
    // Navigate to project detail page with activeTab state for Communication component
    navigate(`/projects/${requestData.projectSeq}/${normalizedType}/${requestData.verificationSeq}`, {
      state: { activeTab: "requests" }
    });
  };

  // Helper functions from Communication component
  const getStatusDotBackground = (status) => {
    switch (status) {
      case "Completed":
        return colors.bgStatusHealthy;
      case "Rejected":
        return colors.bgStatusUnhealthy;
      default:
        return colors.bgStatusUnknown;
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case "Completed":
        return colors.textStatusCompleted;
      case "Rejected":
        return colors.textStatusRejected;
      default:
        return colors.textStatusDefault;
    }
  };

  const renderRequestStatusCell = (status) => {
    if (!status) {
      return <span>-</span>;
    }

    const statusStyles = {
      "PENDING COMPLETION": colors.requestStatusPendingCompletion,
      "PENDING APPROVAL": colors.requestStatusPendingApproval,
      DONE: colors.requestStatusDone,
    };

    // Map status to translation keys
    const statusTranslations = {
      "PENDING COMPLETION": t("project.request.pending-completion"),
      "PENDING APPROVAL": t("project.request.pending-approval"),
      DONE: t("project.request.done"),
    };

    return (
      <span
        className={`inline-flex items-center rounded-2xl px-2 py-1 text-sm leading-[16px] font-semibold ${statusStyles[status] || colors.badgeVariantDefault}`}
        data-testid={fieldValueTestId("status")}
      >
        {statusTranslations[status] || toTitleCase(status)}
      </span>
    );
  };

  // Show loading state
  if (isLoading || (!requestData && !error)) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  // While redirecting to 404, avoid rendering the mock/detail UI (prevents flicker)
  if (isNotFound) {
    return (
      <div data-testid={tid("loading")}>
        <LoadingSpinner data-testid={tid("loading-spinner")} />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        className={`min-h-full ${colors.bgSecondary} flex items-center justify-center`}
        data-testid={tid("error")}
      >
        <div className="text-center" data-testid={tid("error-content")}>
          <p className={`${colors.textDanger} mb-4`} data-testid={tid("error-message")}>Error loading request details</p>
          <p className={`${colors.textSecondary} mb-4`} data-testid={tid("error-detail")}>{error}</p>
          <button
            type="button"
            data-testid={tid("retry-button")}
            onClick={() => window.location.reload()}
            className={`px-4 py-2 rounded-lg ${colors.buttonBackgroundGradient} ${colors.textButton}`}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-full ${colors.bgSecondary}`} data-testid={tid("page")}>
      {/* Header with Breadcrumbs */}
      <div className="pb-5" data-testid={tid("header")}>
        <div className="space-y-3">
          <Breadcrumb
            data-testid={tid("breadcrumb")}
            items={[
              { label: t("project.request.breadcrumb-list"), href: "/requests" },
              { label: t("project.request.breadcrumb-detail") },
            ]}
          />
          <div className="flex items-center justify-between" data-testid={tid("title-row")}>
            <h1 className={`text-2xl font-bold ${colors.textPrimary}`} data-testid={tid("title")}>
              {currentRequestData.requestId}
            </h1>
            <PrimaryButton data-testid={tid("go-to-project-button")} onClick={handleGoToProject}>{t("project.request.go-to-project")}</PrimaryButton>
          </div>
        </div>
      </div>

      {/* Request Summary Accordion */}
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid={tid("summary")}>
        <div
          data-testid={tid("summary-toggle")}
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsRequestSummaryCollapsed(!isRequestSummaryCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsRequestSummaryCollapsed(!isRequestSummaryCollapsed);
            }
          }}
          aria-label={isRequestSummaryCollapsed ? "Expand Request Summary" : "Collapse Request Summary"}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isRequestSummaryCollapsed ? "rotate-180" : ""}`}
            data-testid={tid("summary-chevron")}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid("summary-title")}>
            {t("project.request.request-summary")}
          </h2>
        </div>
        {!isRequestSummaryCollapsed && (
          <div className="relative" data-testid={tid("summary-content")}>
            {/* Content Grid */}
            <div className="space-y-2 px-4" data-testid={tid("summary-grid")}>
              {/* Top Row - Request Details */}
              <div className={`grid grid-cols-5 gap-x-4 gap-y-4 border-b border-dashed ${colors.borderSection} pb-4 pt-3`} data-testid={tid("summary-grid-top")}>
                <div data-testid={fieldContainerTestId("project-id")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("project-id")}>
                    {t("project.request.project-id")}
                  </label>
                  <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.projectId} data-testid={fieldValueTestId("project-id")}>
                    {currentRequestData.projectId}
                  </div>
                </div>
                <div data-testid={fieldContainerTestId("project-name")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("project-name")}>
                    {t("project.request.project-name")}
                  </label>
                  <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.projectName} data-testid={fieldValueTestId("project-name")}>
                    {currentRequestData.projectName}
                  </div>
                </div>
                <div data-testid={fieldContainerTestId("verification-file-name")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("verification-file-name")}>
                    {t("project.request.verification-file-name")}
                  </label>
                  <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.verificationFileName} data-testid={fieldValueTestId("verification-file-name")}>
                    {currentRequestData.verificationFileName}
                  </div>
                </div>
                <div data-testid={fieldContainerTestId("verification-type")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("verification-type")}>
                    {t("project.request.verification-type")}
                  </label>
                  <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.verificationType} data-testid={fieldValueTestId("verification-type")}>
                    {formatVerificationType(currentRequestData.verificationType)}
                  </div>
                </div>
                <div data-testid={fieldContainerTestId("verification-status")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("verification-status")}>
                    {t("project.request.verification")}
                  </label>
                  {renderVerificationStatusCell(currentRequestData.verificationStatusCd, currentRequestData.verificationStatusCdName)}
                </div>
              </div>

              {/* Bottom Row - Request Lifecycle */}
              <div className="grid grid-cols-5 gap-x-4 gap-y-4 pt-4" data-testid={tid("summary-grid-bottom")}>
                <div data-testid={fieldContainerTestId("requester")}>
                  <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("requester")}>
                    {t("project.request.requester")}
                  </label>
                  <div className="min-h-8 flex items-center" data-testid={fieldValueTestId("requester")}>
                    <UserNameWithId name={requestData?.createrName} id={requestData?.createrId} layout="inline" />
                  </div>
                </div>
                {currentRequestData.latestDecision?.status === "Completed" && (
                  <>
                    <div data-testid={fieldContainerTestId("completed-by")}>
                      <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("completed-by")}>
                        {t("project.request.completed-by")}
                      </label>
                      <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.completedBy} data-testid={fieldValueTestId("completed-by")}>
                        {currentRequestData.completedBy}
                      </div>
                    </div>
                    <div data-testid={fieldContainerTestId("completed-at")}>
                      <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("completed-at")}>
                        {t("project.request.completed-at")}
                      </label>
                      <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.completedAt} data-testid={fieldValueTestId("completed-at")}>
                        {currentRequestData.completedAt}
                      </div>
                    </div>
                  </>
                )}
                {currentRequestData.latestDecision?.status === "Rejected" && (
                  <>
                    <div data-testid={fieldContainerTestId("rejected-by")}>
                      <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("rejected-by")}>
                        {t("project.request.rejected-by")}
                      </label>
                      <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.rejectedBy} data-testid={fieldValueTestId("rejected-by")}>
                        {currentRequestData.rejectedBy}
                      </div>
                    </div>
                    <div data-testid={fieldContainerTestId("rejected-at")}>
                      <label className={`block text-sm font-medium ${colors.textSecondary} mb-1`} data-testid={fieldLabelTestId("rejected-at")}>
                        {t("project.request.rejected-at")}
                      </label>
                      <div className={`text-sm ${colors.textPrimary} h-8 leading-8 truncate`} title={currentRequestData.rejectedAt} data-testid={fieldValueTestId("rejected-at")}>
                        {currentRequestData.rejectedAt}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* REQUEST DETAILS Section - Using Communication.jsx patterns */}
      <div className={`mb-4 rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary}`} data-testid={tid("details")}>
        <div
          data-testid={tid("details-toggle")}
          className="mb-2 flex cursor-pointer items-center"
          onClick={() => setIsRequestDetailsCollapsed(!isRequestDetailsCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsRequestDetailsCollapsed(!isRequestDetailsCollapsed);
            }
          }}
          aria-label={isRequestDetailsCollapsed ? "Expand Request Details" : "Collapse Request Details"}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${isRequestDetailsCollapsed ? "rotate-180" : ""}`}
            data-testid={tid("details-chevron")}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`} data-testid={tid("details-title")}>
            {t("project.request.request-details")}
          </h2>
        </div>
        {!isRequestDetailsCollapsed && (
          <div className="px-4 pt-3" data-testid={tid("details-content")}>
            {/* Request Header */}
            <div className="mb-4" data-testid={tid("meta-header")}>
              <div className="flex items-center justify-between mb-3" data-testid={tid("meta-header-top")}>
                <div className="flex items-center gap-4" data-testid={tid("meta-header-left")}>
                  <span className={`text-lg font-bold ${colors.textPrimary}`} data-testid={fieldValueTestId("request-id")}>
                    #{currentRequestData.requestId}
                  </span>
                  <div className={`h-4 w-px ${colors.bgTimeline}`}></div>
                  <span className={`text-sm ${colors.textSecondary}`} data-testid={fieldValueTestId("created-on")}>
                    {t("project.request.created-on")}: {currentRequestData.createdOn}
                  </span>
                </div>
                {renderRequestStatusCell(currentRequestData.status)}
              </div>

              {/* Requester and Assignee */}
              <div className="flex items-center gap-4" data-testid={tid("meta-header-users")}>
                <div className="flex items-center gap-2" data-testid={tid("requester-info")}>
                  <span className={`text-sm ${colors.textSecondary}`} data-testid={fieldLabelTestId("requester-info")}>
                    {t("project.request.requester")}
                  </span>
                  <div className={`flex w-6 h-6 items-center justify-center rounded-full ${colors.bgAvatarBlue}`} data-testid={tid("requester-avatar")}>
                    <span className={`text-sm font-bold ${colors.textPrimary}`} data-testid={tid("requester-avatar-text")}>
                      {currentRequestData.requesterInfo.avatar}
                    </span>
                  </div>
                  <span className={`text-base font-semibold ${colors.textPrimary}`} data-testid={fieldValueTestId("requester-name")}>
                    {currentRequestData.requesterInfo.name}
                  </span>
                  <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldValueTestId("requester-role")}>
                    ({currentRequestData.requesterInfo.role})
                  </span>
                </div>

                <span className={`text-lg ${colors.textSecondary}`} data-testid={tid("users-arrow")}>→</span>

                <div className="flex items-center gap-2" data-testid={tid("assignee-info")}>
                  <span className={`text-sm ${colors.textSecondary}`} data-testid={fieldLabelTestId("assignee-info")}>
                    {t("project.request.assignee")}
                  </span>
                  <div className={`flex w-6 h-6 items-center justify-center rounded-full ${colors.bgAvatarGreen}`} data-testid={tid("assignee-avatar")}>
                    <span className={`text-sm font-bold ${colors.textPrimary}`} data-testid={tid("assignee-avatar-text")}>
                      {currentRequestData.assigneeInfo.avatar}
                    </span>
                  </div>
                  <span className={`text-base font-semibold ${colors.textPrimary}`} data-testid={fieldValueTestId("assignee-name")}>
                    {currentRequestData.assigneeInfo.name}
                  </span>
                  <span className={`text-sm font-normal ${colors.textSecondary}`} data-testid={fieldValueTestId("assignee-role")}>
                    ({currentRequestData.assigneeInfo.role})
                  </span>
                </div>
              </div>
            </div>

            {/* Request Description */}
            <div className="mb-6" data-testid={tid("description")}>
              <p className={`text-sm font-normal leading-relaxed break-words whitespace-normal ${colors.textPrimary}`} data-testid={fieldValueTestId("description")}>
                {currentRequestData.description}
              </p>
            </div>

            {/* Timeline Conversation */}
            {currentRequestData.conversation && currentRequestData.conversation.length > 0 && (
              <div className="relative mb-6" data-testid={tid("conversation")}>
                {/* Vertical timeline line */}
                <div className={`absolute top-0 bottom-0 w-0.5 ${colors.bgTimeline}`} style={{ left: "1.5rem" }} data-testid={tid("conversation-timeline")}></div>

                {currentRequestData.conversation.map((message, index) => (
                  <div
                    key={index}
                    className="relative mb-6 pl-16"
                    data-testid={tid(`conversation-item-${index}`)}
                  >
                    {/* Curved connection line */}
                    <div className="absolute top-4 left-5 h-12 w-12">
                      <div className={`absolute -top-9 left-0 h-10 w-12 rotate-90 rounded-br-3xl border-r-2 border-b-2 ${colors.borderTimeline}`}></div>
                    </div>

                    <div className="mt-12 flex items-start gap-4" data-testid={tid(`conversation-item-${index}-content`)}>
                      {/* Avatar */}
                      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${colors.bgAvatarBlue}`} data-testid={tid(`conversation-item-${index}-avatar`)}>
                        <span className={`text-sm font-bold ${colors.textPrimary}`}>{message.user.avatar}</span>
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1" data-testid={tid(`conversation-item-${index}-body`)}>
                        <div className="mb-3" data-testid={tid(`conversation-item-${index}-header`)}>
                          <div className="flex items-center gap-2">
                            <span className={`text-base font-semibold ${colors.textPrimary}`} data-testid={tid(`conversation-item-${index}-user-name`)}>
                              {message.user.name}
                            </span>
                            <div className={`h-2 w-2 rounded-full ${getStatusDotBackground(message.status)}`} data-testid={tid(`conversation-item-${index}-status-dot`)}></div>
                            <span className={`text-[0.8125rem] font-semibold ${getStatusTextColor(message.status)}`} data-testid={tid(`conversation-item-${index}-status`)}>
                              {t(`communication.${message.status.toLowerCase()}`)}
                            </span>
                          </div>
                          <span className={`text-[0.8125rem] font-normal ${colors.textSecondary}`} data-testid={tid(`conversation-item-${index}-timestamp`)}>
                            {message.timestamp}
                          </span>
                        </div>
                        <p className={`text-sm font-normal break-words whitespace-normal ${colors.textPrimary}`} data-testid={tid(`conversation-item-${index}-message`)}>
                          {message.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Completion Request Date */}
            <div
              className={`flex items-center gap-2 rounded-lg ${colors.bgCompletionRequestDate} px-4 py-1.5 w-fit`}
              data-testid={fieldContainerTestId("completion-request-date")}
            >
              <svg className={`h-4 w-4 ${colors.textStatusUnconfigured}`} fill="currentColor" viewBox="0 0 20 20" data-testid={tid("completion-request-date-icon")}>
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z"
                  clipRule="evenodd"
                />
              </svg>
              <span className={`text-sm font-semibold ${colors.textCompletionRequestDate}`} data-testid={fieldValueTestId("completion-request-date")}>
                {t("project.request.completion-request-date")}: {currentRequestData.completionRequestDate}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetail;
